/**
 * Spotify OAuth PKCE Authentication for Loud Neighbors
 */

import { isNative } from '../utils/platform';

const SPOTIFY_CLIENT_ID = import.meta.env.VITE_LN_SPOTIFY_CLIENT_ID || import.meta.env.VITE_SPOTIFY_CLIENT_ID;

// On native: custom URL scheme → Spotify accepts it, deep link fires appUrlOpen.
// The production app is loudneighbors://, the staging app loudneighbors-staging://
// (set per build mode in .env.*; must match the iOS build configuration's scheme).
// On web: env var or current origin.
const SPOTIFY_REDIRECT_URI = isNative
  ? (import.meta.env.VITE_LN_NATIVE_REDIRECT_URI || 'loudneighbors://callback')
  : (import.meta.env.VITE_LN_SPOTIFY_REDIRECT_URI || `${window.location.origin}/callback`);

import { API_BASE } from './api';

const SPOTIFY_AUTH_URL = 'https://accounts.spotify.com/authorize';

const SCOPES = [
  'user-read-recently-played',
  'user-top-read',
  'user-follow-read',
  'user-read-private',
  'user-read-email',
  'user-read-playback-state',
  'user-read-currently-playing',
].join(' ');

function generateCodeVerifier(length = 64) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const randomValues = crypto.getRandomValues(new Uint8Array(length));
  for (let i = 0; i < length; i++) {
    result += chars[randomValues[i] % chars.length];
  }
  return result;
}

async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  let digest;
  if (crypto.subtle) {
    digest = await crypto.subtle.digest('SHA-256', data);
  } else {
    // Fallback for non-secure contexts (HTTP dev server, Capacitor WebView)
    const { sha256 } = await import('./sha256-fallback');
    digest = sha256(data);
  }
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateState() {
  return generateCodeVerifier(16);
}

// Pending sign-ins, keyed by state: { [state]: { verifier, at } }.
//
// One entry per login attempt, not a single slot: tapping "Sign in" twice
// used to overwrite the first attempt's state, so its callback failed with
// "State mismatch". localStorage, not sessionStorage, so it survives iOS
// discarding the WebView while the user is in the Spotify sheet. Entries
// expire after PENDING_TTL_MS and are removed when used.
const PENDING_KEY = 'ln_pending_logins';
const PENDING_TTL_MS = 15 * 60 * 1000;

function readPending() {
  try {
    const all = JSON.parse(localStorage.getItem(PENDING_KEY)) || {};
    const now = Date.now();
    for (const s of Object.keys(all)) {
      if (!all[s] || now - all[s].at > PENDING_TTL_MS) delete all[s];
    }
    return all;
  } catch {
    return {};
  }
}

function writePending(all) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(all));
  } catch {
    // Storage unavailable: the callback will report an expired sign-in.
  }
}

export function clearPendingLogins() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // nothing to clear
  }
}

// The callback's state matches no pending sign-in: already used (the same
// callback delivered twice), expired, or from another device or browser.
// CallbackPage treats it as "already signed in" or "try again", not an error.
export class StaleCallbackError extends Error {
  constructor() {
    super('This sign-in link has expired or was already used. Please sign in again.');
    this.name = 'StaleCallbackError';
  }
}

export async function initiateSpotifyLogin() {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  const pending = readPending();
  pending[state] = { verifier: codeVerifier, at: Date.now() };
  writePending(pending);
  if (!sessionStorage.getItem('ln_redirect_after')) {
    sessionStorage.setItem('ln_redirect_after', window.location.pathname);
  }

  const params = new URLSearchParams({
    client_id: SPOTIFY_CLIENT_ID,
    response_type: 'code',
    redirect_uri: SPOTIFY_REDIRECT_URI,
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
    state: state,
    scope: SCOPES,
  });

  const authUrl = `${SPOTIFY_AUTH_URL}?${params.toString()}`;

  if (isNative) {
    // Open in system browser (SFSafariViewController / Chrome Custom Tabs) so the
    // custom-scheme redirect (loudneighbors://callback?...) triggers the OS deep link
    // handler and fires appUrlOpen in the app.
    console.log('[spotifyAuth] opening Browser with:', authUrl);
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: authUrl });
      console.log('[spotifyAuth] Browser.open resolved');
    } catch (e) {
      console.error('[spotifyAuth] Browser.open failed:', e);
      window.location.href = authUrl;
    }
  } else {
    window.location.href = authUrl;
  }
}

export async function handleSpotifyCallback(code, state) {
  // CSRF check: the state must be one this app generated and hasn't used.
  const pending = readPending();
  const entry = state ? pending[state] : null;
  if (!entry) {
    throw new StaleCallbackError();
  }
  // Single use, removed before the exchange so a duplicate delivery of the
  // same callback can't run it twice.
  delete pending[state];
  writePending(pending);
  const codeVerifier = entry.verifier;

  const response = await fetch(`${API_BASE}/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      code,
      code_verifier: codeVerifier,
      redirect_uri: SPOTIFY_REDIRECT_URI,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to exchange authorization code');
  }

  const result = await response.json();

  // Store auth token for native app (cookies don't work in Capacitor WebView)
  if (result.auth_token) {
    const { setAuthToken } = await import('./api');
    setAuthToken(result.auth_token);
  }

  return result;
}
